import React, {useEffect, useState} from 'react';
import {StyleSheet, TouchableOpacity, View} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import Spacer from '@/Atoms/Spacer';
import {FontSizes, vs} from '@/Utils/Dimensions';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import useArenaStore from '@/Store/ArenaStore';
import useGetArenaGames, {ArenaOngoingGame} from '../hooks/useGetArenaGames';
import PlayerAvatar from '@/Screens/Leaderboard/components/PlayerAvatar';
import {
  lbColors,
  lbRadius,
  lbShadow,
  avatarColorForUser,
} from '@/Screens/Leaderboard/leaderboardTheme';
import {initialsForName} from '@/Screens/Leaderboard/useLeaderboardData';
import ArenaSectionHeader from './ArenaSectionHeader';
import Pagination from '@/Screens/Games/Pagination';

// ── ArenaGames ───────────────────────────────────────────────────────────────
// The "Live games" arena tab: the ongoing-boards list, split out of ArenaLobby
// so the leaderboard tab stays uncluttered. Owns useGetArenaGames (polls the
// game service's /arenaGames REST route every 10s while the arena is running).
// Themed to the wins-leaderboard look (PlayerAvatar identity discs, white
// cards, parchment ground). Press a card to spectate — same watchTournamentGame
// plumbing games.tsx rows use; a player who is mid-game (`playing`) must not
// spectate-jump, so their cards go muted/non-pressable with a one-line hint.
//
// Paged (10/page): a full arena runs hundreds of boards at once. Ordering is
// the server's durable game_seq, so board numbers are global and stable across
// pages. The caller's own board is pinned above the list whatever page they are
// on — a player should never have to hunt for their own game.

const FIRE_COLOR = '#FF6B00';
const PAGE_SIZE = 10;

// One side of the matchup: colored identity avatar + a small white/black side
// dot + name + muted rating + ⚡ when that side berserked.
const SideRow: React.FC<{
  side: 'white' | 'black';
  id: string;
  name: string;
  rating: number;
  berserk?: boolean;
}> = ({side, id, name, rating, berserk}) => (
  <Box flexDirection="row" alignItems="center" gap={8}>
    <PlayerAvatar
      initials={initialsForName(name)}
      color={avatarColorForUser(id)}
      size={30}
    />
    <View
      style={[
        styles.sideDot,
        side === 'white' ? styles.sideDotWhite : styles.sideDotBlack,
      ]}
    />
    <CCText
      fontSize={FontSizes[13]}
      fontWeight="semibold"
      numberOfLines={1}
      style={{maxWidth: 150, color: lbColors.ink}}>
      {name}
    </CCText>
    <CCText fontSize={FontSizes[11]} style={{color: lbColors.muted}}>
      {rating}
    </CCText>
    {berserk ? (
      <CCText fontSize={FontSizes[12]} style={{color: FIRE_COLOR}}>
        ⚡
      </CCText>
    ) : null}
  </Box>
);

const ArenaGames: React.FC<{
  tournamentId?: string | number;
  loggedInUserId?: string | number;
  watchTournamentGame?: (
    pairingKey: string,
    tournamentId: string | number,
    round: number,
  ) => void;
  playing?: boolean;
}> = ({tournamentId, loggedInUserId, watchTournamentGame, playing}) => {
  const [page, setPage] = useState(1);
  // A different arena must not inherit the previous one's page.
  useEffect(() => {
    setPage(1);
  }, [tournamentId]);

  const {
    games: ongoingGames,
    myGame,
    total,
    totalPages,
  } = useGetArenaGames(tournamentId, page, PAGE_SIZE, loggedInUserId);
  const arenaState = useArenaStore(state => state.status?.state);
  const running = arenaState === 'running';
  const isBlocked = Boolean(playing) || !watchTournamentGame;

  // Boards drain as the arena ends, so a deep pager can outlive its pages.
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const renderCard = (
    game: ArenaOngoingGame,
    index: number,
    opts?: {pinned?: boolean},
  ) => {
    const onPress = () => {
      // mirrors the !playing guard on games.tsx row presses — a player who is
      // mid-game must not spectate-jump
      if (!playing && watchTournamentGame && tournamentId) {
        watchTournamentGame(
          `pairing:${game.whiteId}-${game.blackId}`,
          tournamentId,
          0,
        );
      }
    };
    // Board numbers are global, not page-relative: the server orders by the
    // durable game_seq, so board 11 stays board 11 on page 2.
    const boardNo = game.gameSeq ?? (page - 1) * PAGE_SIZE + index + 1;
    return (
      <TouchableOpacity
        key={`${opts?.pinned ? 'pinned-' : ''}${game.tableId}`}
        activeOpacity={0.7}
        style={[
          styles.gameCard,
          isBlocked && styles.gameCardMuted,
          opts?.pinned && styles.gameCardPinned,
        ]}
        disabled={isBlocked}
        onPress={onPress}
        data-testid="arena-ongoing-game-row">
        <Box style={{flexShrink: 1}} gap={8}>
          <Box flexDirection="row" alignItems="center" gap={6}>
            <CCText style={styles.boardLabel}>Board {boardNo}</CCText>
            {opts?.pinned && (
              <View style={styles.yourGameTag}>
                <CCText
                  fontSize={FontSizes[10]}
                  fontWeight="bold"
                  color="#FFFFFF">
                  YOUR GAME
                </CCText>
              </View>
            )}
          </Box>
          <SideRow
            side="white"
            id={game.whiteId}
            name={game.whiteUsername}
            rating={game.whiteRating}
            berserk={game.whiteBerserk}
          />
          <SideRow
            side="black"
            id={game.blackId}
            name={game.blackUsername}
            rating={game.blackRating}
            berserk={game.blackBerserk}
          />
        </Box>
        {!isBlocked && (
          <View style={styles.watchBtn} data-testid="arena-watch-btn">
            <MaterialCommunityIcons
              name="binoculars"
              size={20}
              color="#FFFFFF"
            />
          </View>
        )}
      </TouchableOpacity>
    );
  };

  // Authoritative live-board count from the server (COUNT over the live
  // pairings), not the length of the page we happen to be showing.
  const liveCount = total || ongoingGames.length;
  const countLabel =
    liveCount > 0
      ? `${liveCount} game${liveCount === 1 ? '' : 's'} in progress`
      : undefined;

  // Pin the caller's own board only when it is not already on this page —
  // otherwise it would render twice.
  const pinnedGame = myGame && !myGame.inPage ? myGame : null;

  return (
    <Box style={styles.container}>
      <ArenaSectionHeader
        title="Live games"
        live={running}
        subtitle={countLabel}
      />
      {isBlocked && ongoingGames.length > 0 && (
        <>
          <Spacer spacing={vs(6)} />
          <CCText
            fontSize={FontSizes[12]}
            style={{marginLeft: 4, color: lbColors.muted}}
            data-testid="arena-watch-blocked-hint">
            Finish your game to watch others.
          </CCText>
        </>
      )}
      <Spacer spacing={vs(10)} />
      {pinnedGame && renderCard(pinnedGame, 0, {pinned: true})}
      {ongoingGames.length > 0 ? (
        <>
          {ongoingGames.map((g, i) => renderCard(g, i))}
          {totalPages > 1 && (
            <>
              <Spacer spacing={vs(8)} />
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                paginate={setPage}
                maxPage={7}
                activeColor
              />
            </>
          )}
        </>
      ) : (
        <Box style={styles.emptyState}>
          <Box style={styles.emptyIconBadge}>
            <MaterialCommunityIcons
              name="chess-pawn"
              size={26}
              color={lbColors.goldDeep}
            />
          </Box>
          <Spacer spacing={vs(10)} />
          <CCText
            fontSize={FontSizes[13]}
            fontWeight="semibold"
            style={{color: lbColors.inkSoft}}>
            {arenaState === 'finished'
              ? 'This arena has finished.'
              : 'No games in progress right now.'}
          </CCText>
          <Spacer spacing={vs(3)} />
          <CCText
            fontSize={FontSizes[11]}
            style={{color: lbColors.muted, textAlign: 'center'}}>
            {/* A finished arena has no live boards BY DEFINITION — saying
                "games will show up once it's live" would be a lie. */}
            {arenaState === 'finished'
              ? 'See the final standings on the Leaderboard tab.'
              : running
              ? 'New pairings appear here the moment they start.'
              : 'Games will show up here once the arena is live.'}
          </CCText>
        </Box>
      )}
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 8,
    paddingBottom: 16,
    backgroundColor: lbColors.bg,
  },
  gameCard: {
    backgroundColor: lbColors.card,
    borderRadius: lbRadius.lg,
    paddingVertical: 14,
    paddingHorizontal: 15,
    marginBottom: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: lbColors.line,
    borderLeftWidth: 3,
    borderLeftColor: lbColors.teal,
    ...lbShadow.card,
  },
  gameCardMuted: {
    opacity: 0.5,
  },
  // the caller's own board, pinned above the page
  gameCardPinned: {
    borderWidth: 1.5,
    borderColor: lbColors.teal,
    backgroundColor: lbColors.youRowBg,
  },
  yourGameTag: {
    backgroundColor: lbColors.teal,
    paddingHorizontal: 7,
    paddingVertical: 1,
    borderRadius: lbRadius.pill,
  },
  boardLabel: {
    fontSize: FontSizes[10],
    fontWeight: 'bold',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: lbColors.faint,
  },
  sideDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  sideDotWhite: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1.5,
    borderColor: lbColors.faint,
  },
  sideDotBlack: {
    backgroundColor: lbColors.ink,
  },
  watchBtn: {
    width: 42,
    height: 42,
    borderRadius: 21,
    marginLeft: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: lbColors.teal,
    shadowColor: lbColors.teal,
    shadowOpacity: 0.34,
    shadowRadius: 9,
    shadowOffset: {width: 0, height: 3},
    elevation: 3,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 28,
    paddingHorizontal: 20,
    backgroundColor: lbColors.card,
    borderRadius: lbRadius.xl,
    borderWidth: 1,
    borderColor: lbColors.line,
    ...lbShadow.card,
  },
  emptyIconBadge: {
    width: 52,
    height: 52,
    borderRadius: 26,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: lbColors.goldSoft,
  },
});

export default ArenaGames;
